"use client";
import { z } from "zod";
import { useState } from "react";
import axios from "@config/axios";
import { AxiosError } from "axios";
import { encrypt } from "@lib/encrypt";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import { Input } from "@components/ui/input";
import { toast } from "@components/ui/toaster";
import { Button } from "@components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import usePasswordToggle from "@hooks/usePasswordToggle";
import { LockKeyhole, RefreshCcwDot } from "lucide-react";
import { ResetPasswordSchema } from "@schemas/resetPassword";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";

type ResetPasswordFormProps = {
	email: string;
	otp: string;
};

const ResetPasswordForm = ({ email, otp }: ResetPasswordFormProps) => {
	const router = useRouter();
	const [onResetToast, setOnResetToast] = useState<string | number>();
	const { inputIcon: pwdInputIcon, inputType: pwdInputType } = usePasswordToggle();
	const { inputIcon: cPwdInputIcon, inputType: cPwdInputType } = usePasswordToggle();

	const defaultValues: z.infer<typeof ResetPasswordSchema> = {
		newPassword: "",
		confirmPassword: "",
	};

	const form = useForm<z.infer<typeof ResetPasswordSchema>>({
		defaultValues: defaultValues,
		mode: "onTouched",
		resolver: zodResolver(ResetPasswordSchema),
	});

	const onReset = async (values: z.infer<typeof ResetPasswordSchema>) => {
		setOnResetToast(toast.loading("Loading...", { description: "Resetting password..." }));
		const password = encrypt(values?.newPassword);
		await axios.post("/api/user/reset-password", { email, otp, password });
	};

	const { mutate, isPending } = useMutation({
		mutationFn: onReset,
		onSuccess: () => {
			toast.success("Success!", { id: onResetToast, description: "Password reset successfully!" });
			router.replace("/auth/login");
		},
		onError: (error: unknown) => {
			toast.error("Error!", { id: onResetToast, description: (error as AxiosError<string>)?.response?.data || "Internal server error!" });
		},
	});

	return (
		<Form {...form}>
			<form className="flex w-full max-w-sm flex-col items-center justify-stretch gap-7" onSubmit={form.handleSubmit(values => mutate(values))}>
				<div className="flex w-full flex-col items-center justify-center gap-6">
					<FormField
						control={form.control}
						name="newPassword"
						render={({ field }) => (
							<FormItem className="w-full">
								<FormLabel>
									New Password<span className="text-destructive">*</span>
								</FormLabel>
								<FormControl>
									<Input endContent={pwdInputIcon} placeholder="Enter your new password" startContent={<LockKeyhole size={16} />} type={pwdInputType} {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="confirmPassword"
						render={({ field }) => (
							<FormItem className="w-full">
								<FormLabel>
									Confirm Password<span className="text-destructive">*</span>
								</FormLabel>
								<FormControl>
									<Input endContent={cPwdInputIcon} placeholder="Confirm your new password" startContent={<LockKeyhole size={16} />} type={cPwdInputType} {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				<div className="flex w-full flex-col items-center justify-between gap-3">
					<Button className="w-36 justify-between" disabled={isPending} size="lg" type="submit" variant="default">
						Reset
						<RefreshCcwDot size={16} />
					</Button>
				</div>
			</form>
		</Form>
	);
};

export default ResetPasswordForm;
